const originKey = 'lon:catalog-origin';
const returnKey = 'lon:catalog-return';
const productStateKey = 'lonProductNavigation';
const originStateKey = 'lonCatalogNavigation';
const contextLifetime = 30 * 60 * 1000;
const arrivalLifetime = 60 * 1000;
const localePrefix = '(?:[a-z]{2}(?:-[a-z]{2})?/)?';
const browsePattern = new RegExp(`^/${localePrefix}(?:collections(?:/[a-z0-9-]+(?:/[a-z0-9+%_-]+)?)?|search)?/?$`, 'i');
const productPattern = new RegExp(`^/${localePrefix}(?:collections/[a-z0-9-]+/)?products/[^/]+/?$`, 'i');

function localUrl(value, pattern) {
  try {
    if (!value || typeof value !== 'string' || value.length > 4096) return null;
    const url = new URL(value, location.origin);
    if (url.origin !== location.origin || url.username || url.password || /%2f|%5c/i.test(url.pathname) || !pattern.test(url.pathname)) return null;
    return url;
  } catch { return null; }
}

function browseUrl(value) {
  const url = localUrl(value, browsePattern);
  if (!url || url.searchParams.getAll('view').length > 1 || url.searchParams.has('section_id')) return null;
  if (url.searchParams.has('return_to')) url.searchParams.delete('return_to');
  return url;
}

function productUrl(value) {
  return localUrl(value, productPattern);
}

function localHref(url) {
  return url.pathname + url.search + url.hash;
}

function validContext(value, lifetime = contextLifetime) {
  const age = Date.now() - value?.time;
  if (!value || !Number.isFinite(age) || age < 0 || age > lifetime || !browseUrl(value.returnTo) || !productUrl(value.productPath)) return null;
  return value;
}

function readContext(key, lifetime) {
  try { return validContext(JSON.parse(sessionStorage.getItem(key)), lifetime); } catch { return null; }
}

function writeContext(key, value) {
  try { sessionStorage.setItem(key, JSON.stringify(value)); } catch {}
}

function removeContext(key) {
  try { sessionStorage.removeItem(key); } catch {}
}

function saveHistory(key, value) {
  try { history.replaceState({ ...history.state, [key]: value }, '', location.href); } catch {}
}

function withOrigin(value, returnTo) {
  const url = returnTo && productUrl(value);
  if (!url) return null;
  url.searchParams.set('return_to', returnTo);
  return localHref(url);
}

function unmodifiedClick(event, link) {
  return !event.defaultPrevented && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && !link.hasAttribute('download') && (!link.target || link.target === '_self');
}

function sameReferrer(url) {
  const referrer = browseUrl(document.referrer);
  return referrer?.pathname === url.pathname && referrer.search === url.search;
}

function originLabel() {
  return (document.querySelector('.campaign-lookbook__masthead h1, .collection-header h1')?.textContent || document.body.dataset.navigationLabel || '').trim().slice(0, 160);
}

export function connectCatalogOrigin(root, signal) {
  if (root.dataset.editor === 'true' || window.Shopify?.designMode) return;
  if (root !== document.body && document.body.dataset.catalogNavigationConnected === 'true') return;
  if (root === document.body) root.dataset.catalogNavigationConnected = 'true';
  const active = () => !signal.aborted && (root === document.body || document.body.dataset.catalogNavigationConnected !== 'true');
  const contextFor = card => {
    const url = new URL(location.href);
    const panel = card.closest('[data-lookbook-panel]');
    if (panel?.id) url.hash = panel.id;
    const validated = browseUrl(url.href);
    return validated ? localHref(validated) : null;
  };
  const prepare = card => {
    const returnTo = contextFor(card);
    if (!returnTo) return null;
    const destination = productUrl(card.querySelector('[data-card-link]')?.getAttribute('href'));
    if (!card.id && destination) {
      const scope = card.closest('[data-lookbook-panel]')?.id || card.closest('.shopify-section')?.id || 'catalog';
      const pair = card.closest('[data-lookbook-pair]')?.getAttribute('data-lookbook-pair');
      card.id = `CatalogOrigin-${pair ? `${scope}-${pair}` : scope}-${encodeURIComponent(destination.pathname)}`;
    }
    card.querySelectorAll('[data-card-link]').forEach(link => {
      const href = withOrigin(link.getAttribute('href'), returnTo);
      if (href) link.setAttribute('href', href);
    });
    const form = card.querySelector('[data-card-form]');
    if (form) {
      const href = withOrigin(form.dataset.productUrl, returnTo);
      if (href) form.dataset.productUrl = href;
      let field = form.querySelector('input[name="return_to"]');
      if (form.method === 'get' && href) {
        form.action = href;
        if (!field) {
          field = document.createElement('input');
          field.type = 'hidden';
          field.name = 'return_to';
          form.append(field);
        }
        field.value = returnTo;
      }
      if (field) field.disabled = form.method !== 'get';
    }
    return returnTo;
  };
  const remember = (card, link, returnTo, position, label = originLabel()) => {
    const destination = productUrl(link?.getAttribute('href') || card?.querySelector('[data-card-form]')?.dataset.productUrl);
    if (!destination || !returnTo) return;
    const saved = {
      productPath: destination.pathname, returnTo, label, cardId: card?.id,
      linkIndex: card ? Math.max(0, Array.from(card.querySelectorAll('[data-card-link]')).indexOf(link)) : 0,
      x: position?.x ?? window.scrollX, y: position?.y ?? window.scrollY, time: Date.now(),
    };
    writeContext(originKey, saved);
    saveHistory(originStateKey, saved);
  };
  root.querySelectorAll('product-card').forEach(prepare);
  ['pointerdown', 'focusin', 'contextmenu', 'click', 'auxclick', 'submit'].forEach(type => {
    root.addEventListener(type, event => {
      if (!active()) return;
      const suggestion = event.target.closest?.('.header-search__product');
      if (suggestion && root.contains(suggestion)) {
        const search = suggestion.closest('[data-header-search]');
        const url = browseUrl(search?.querySelector('form')?.getAttribute('action'));
        const query = search?.querySelector('[data-search-input]')?.value?.trim();
        if (!url || !/\/search\/?$/.test(url.pathname) || !query) return;
        url.searchParams.set('q', query);
        const returnTo = localHref(url);
        const href = withOrigin(suggestion.getAttribute('href'), returnTo);
        if (href) suggestion.setAttribute('href', href);
        if (['click', 'auxclick', 'contextmenu'].includes(type)) remember(null, suggestion, returnTo, null, document.body.dataset.navigationSearchLabel);
        return;
      }
      const card = event.target.closest?.('product-card');
      if (!card || !root.contains(card)) return;
      const returnTo = prepare(card);
      const link = event.target.closest?.('[data-card-link]');
      if (link && ['click', 'auxclick', 'contextmenu'].includes(type) || type === 'submit' && event.target.matches('[data-card-form]') && event.target.method === 'get') remember(card, link, returnTo);
    }, { capture: true, signal });
  });
  const prepareQuickAdd = event => {
    if (!active()) return;
    const modal = event.target.closest?.('quick-add-dialog');
    const card = modal?.trigger?.closest('product-card');
    if (!card || !root.contains(card)) return;
    const link = modal.querySelector('[data-quick-add-link]');
    const returnTo = contextFor(card);
    const href = link && withOrigin(link.getAttribute('href'), returnTo);
    if (href) link.setAttribute('href', href);
    if (event.type === 'click' && event.target.closest?.('[data-quick-add-link]')) remember(card, link, returnTo, modal.scrollPosition);
  };
  document.addEventListener('quick-add:ready', prepareQuickAdd, { signal });
  document.addEventListener('product:option-change', prepareQuickAdd, { signal });
  document.addEventListener('click', prepareQuickAdd, { capture: true, signal });

  const restore = (fromHistory = false) => {
    if (!active()) return;
    const saved = readContext(returnKey) || (fromHistory ? validContext(history.state?.[originStateKey]) : null);
    removeContext(returnKey);
    if (!saved || saved.returnTo !== localHref(new URL(location.href))) return;
    let frame = requestAnimationFrame(() => {
      frame = requestAnimationFrame(() => {
        if (!active() || !root.isConnected || document.activeElement !== document.body) return;
        const card = document.getElementById(saved.cardId);
        const link = root.contains(card) && card.querySelectorAll('[data-card-link]')[saved.linkIndex];
        if (!link || productUrl(link.getAttribute('href'))?.pathname !== saved.productPath) return;
        link.focus({ preventScroll: true });
        if (Number.isFinite(saved.x) && Number.isFinite(saved.y) && saved.x >= 0 && saved.y >= 0) window.scrollTo({ left: saved.x, top: saved.y, behavior: 'instant' });
      });
    });
    signal.addEventListener('abort', () => cancelAnimationFrame(frame), { once: true });
  };
  const restoreInitial = () => restore(window.performance?.getEntriesByType('navigation')[0]?.type === 'back_forward');
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', restoreInitial, { once: true, signal });
  else restoreInitial();
  window.addEventListener('pageshow', event => { if (event.persisted) restore(true); }, { signal });
}

class ProductNavigation extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    if (this.dataset.editor === 'true' || window.Shopify?.designMode) return;
    const source = new URL(location.href);
    if (source.searchParams.getAll('return_to').length !== 1) return;
    const returnTo = browseUrl(source.searchParams.get('return_to'));
    if (!returnTo) return;
    const href = localHref(returnTo);
    const matches = value => value?.productPath === source.pathname && value.returnTo === href;
    const previous = validContext(history.state?.[productStateKey]);
    const arriving = readContext(originKey, arrivalLifetime);
    const saved = matches(previous) ? previous : matches(arriving) ? arriving : null;
    if (!saved && !sameReferrer(returnTo)) return;
    const context = saved || { productPath: source.pathname, returnTo: href, time: Date.now() };
    if (matches(arriving)) removeContext(originKey);
    saveHistory(productStateKey, context);
    const label = typeof context.label === 'string' && context.label.trim()
      ? context.label.trim().slice(0, 160)
      : /\/search\/?$/.test(returnTo.pathname) ? this.dataset.searchLabel
        : /\/collections(?:\/|$)/.test(returnTo.pathname) ? this.dataset.catalogLabel : this.dataset.homeLabel;
    this.querySelectorAll('[data-product-origin]').forEach(link => {
      link.setAttribute('href', href);
      link.textContent = label;
    });
    this.querySelectorAll('[data-product-canonical]').forEach(path => {
      path.classList.add('visually-hidden');
      path.setAttribute('inert', '');
      path.setAttribute('aria-hidden', 'true');
    });
    this.querySelectorAll('[data-product-context-path]').forEach(path => { path.hidden = false; });
    this.querySelectorAll('[data-product-return], [data-product-origin]').forEach(link => {
      link.setAttribute('href', href);
      link.hidden = false;
      const text = link.querySelector('span');
      if (text && link.hasAttribute('data-product-return')) text.textContent = this.dataset.returnLabel;
      link.addEventListener('click', event => {
        if (unmodifiedClick(event, link) && context.cardId) writeContext(returnKey, context);
      }, { signal });
    });
    this.closest('product-detail')?.addEventListener('product:variant-change', () => {
      queueMicrotask(() => {
        if (signal.aborted) return;
        const current = new URL(location.href);
        if (current.pathname !== source.pathname) return;
        current.searchParams.set('return_to', href);
        history.replaceState(history.state, '', current);
      });
    }, { signal });
  }

  disconnectedCallback() {
    this.controller?.abort();
  }
}

if (!customElements.get('product-navigation')) customElements.define('product-navigation', ProductNavigation);
if (document.body?.dataset.navigationPage) connectCatalogOrigin(document.body, new AbortController().signal);
